import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    useTheme,
} from '@mui/material';
import Box from '@/components/ui/Box/Box';
import Button from '@/components/ui/Button/Button';
import Typography from '@/components/ui/Typography/Typography';
import type { CatalogEntry } from '@/api/catalog';
import type { ConflictDialogProps } from './IConflictDialog';
import { styles } from './styles';

const formatPrice = (n: number | null | undefined): string => {
    if (n == null) return 'Quote Required';
    return `$${n.toFixed(2)}`;
};

const findCatalogEntry = (
    catalog: CatalogEntry[],
    serviceDetailId: number,
): CatalogEntry | undefined =>
    catalog.find((c) => c.id === serviceDetailId);

const ConflictDialog = ({
    open,
    conflictData,
    catalog,
    onCancel,
    onForceUpdate,
}: ConflictDialogProps) => {
    const theme = useTheme();
    const updates = conflictData?.payload?.rulesToUpdate ?? [];

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
            <DialogTitle>Rule Conflict Detected</DialogTitle>
            <DialogContent>
                <Typography sx={styles.intro}>
                    {conflictData?.message ??
                        'The rule you are trying to add conflicts with existing rules.'}
                </Typography>
                {updates.length > 0 && (
                    <>
                        <Typography sx={styles.sectionTitle}>
                            Rules that will be updated:
                        </Typography>
                        {updates.map((item, idx) => {
                            const entry = findCatalogEntry(
                                catalog,
                                item.rule.service_detail_id,
                            );
                            return (
                                <Box key={idx} sx={styles.item}>
                                    <Typography sx={styles.label}>
                                        {item.rule.description ?? `Rule #${item.rule.id}`}
                                    </Typography>
                                    <Box sx={styles.grid}>
                                        <Box>
                                            <Typography sx={styles.label}>Service:</Typography>
                                            <Typography sx={styles.value}>
                                                {entry?.service_type ?? '-'}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography sx={styles.label}>Repair Type:</Typography>
                                            <Typography sx={styles.value}>
                                                {entry?.repair_type ?? '-'}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography sx={styles.label}>Part:</Typography>
                                            <Typography sx={styles.value}>
                                                {entry?.part ?? '-'}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography sx={styles.label}>Position:</Typography>
                                            <Typography sx={styles.value}>
                                                {entry?.position ?? '-'}
                                            </Typography>
                                        </Box>
                                        {item.fieldsToUpdate.price !== undefined && (
                                            <Box sx={{ gridColumn: '1 / -1' }}>
                                                <Typography sx={styles.label}>
                                                    Price Change:
                                                </Typography>
                                                <Typography sx={styles.value}>
                                                    <Box component="span" sx={styles.priceOld}>
                                                        {formatPrice(item.rule.price)}
                                                    </Box>
                                                    →{' '}
                                                    <Box component="span" sx={styles.priceNew}>
                                                        {formatPrice(item.fieldsToUpdate.price)}
                                                    </Box>
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}
                    </>
                )}
                <Typography sx={styles.warning(theme)}>
                    Click &lsquo;Force Update&rsquo; to proceed with these changes, or
                    &lsquo;Cancel&rsquo; to abort.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>Cancel</Button>
                <Button onClick={onForceUpdate} variant="contained" color="warning">
                    Force Update
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConflictDialog;
